import * as React from "react"
import { CartStructure, SaleorError } from "../../interfaces/SaleorTypes"
import { Col, Container, Row } from "react-grid-system"
import Card from "../../atoms/Card"
import H1 from "../../atoms/typography/H1"
import { HorizontalSpacer } from "../../atoms/spacers"
import H4 from "../../atoms/typography/H4"
import H2 from "../../atoms/typography/H2"
import styled from "styled-components"
import RentalOrderSummary from "../../molecules/store/RentalMolecules/OrderSummary"
import SupportInfo from "../../molecules/store/RentalMolecules/SupportInfo"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import { useUser } from "../../../hooks/useUser"
import { theme } from "../../theme"
import RegisterAccountFormRental from "../../molecules/store/RentalMolecules/RegisterAccountForm"
import RentalShippingForm from "../../molecules/store/RentalMolecules/RentalShippingForm"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { AnimatePresence } from "framer-motion"
import Modal from "../../molecules/Modals/Modal"
import LoginModalContainer from "../../organisms/LoginScreens/LoginModalContainer"
import { StripeAppWidget, StripePluginWidget } from "../../store/checkout/CheckoutStripeWidget"
import { useCreateCheckoutMutation } from "../../../hooks-saleor/checkoutCreateMutation"
import { useCheckoutPaymentCreate } from "../../../hooks-saleor/useCheckoutPaymentCreate"
import { useCheckoutCompleteMutation } from "../../../hooks-saleor/checkoutCompleteMutation"
import { useStripeTransactionInitialize } from "../../../hooks-saleor/useStripeTransactionInitialize"
import { useOrderUpdateMetadataMutation } from "../../../hooks-saleor/orderUpdateMetadataMutation"
import { SELECTED_CHANNEL_SLUG } from "../../../utils/saleor-constants"
import BillingAddressForm from "../../molecules/store/RentalMolecules/BillingAddressForm"
import BodyMedium from "../../atoms/typography/BodyMedium"
import SmallButton from "../../atoms/buttons/SmallButton"
import { ArrowBack } from "../../atoms/icons/ArrowBack"
import { pushAddToCartEvent } from "../../../utils/datalayer"
import { IS_USING_STRIPE_PLUGIN_GATEWAY } from "../../../utils/saleor-constants"

interface Props {
  rentalOrder: CartStructure[]
  selectedDate: string
  onClickBack: () => void
}

const PaddedCard = styled(Card)`
  padding: 32px;
`

export interface BillingInfo {
  bFirstName: string
  bLastName: string
  bStreetAdress1: string
  bStreetAdress2?: string
  bPostalCode: string
  bCity: string
  bCountry: string
  bCountryArea?: string
  bPhone: string
  bCompany: string
  bEmail: string
}

export interface ShippingInfo {
  sFirstName: string
  sLastName: string
  sStreetAdress1: string
  sStreetAdress2?: string
  sCity: string
  sPostalCode: string
  sCountry: string
  sCountryArea?: string
  sPhone: string
}

const BookRentalPage: React.FC<Props> = ({ rentalOrder, selectedDate, onClickBack }) => {
  const { user, loading: userLoading } = useUser()

  const { createCheckoutMutation, data } = useCreateCheckoutMutation()
  const { checkoutPaymentCreate } = useCheckoutPaymentCreate()
  const { checkoutCompleteMutation } = useCheckoutCompleteMutation()
  const { stripeTransactionInitialize } = useStripeTransactionInitialize()
  const { orderUpdateMetadataMutation } = useOrderUpdateMetadataMutation()

  const [errors, setErrors] = React.useState<SaleorError[]>([])
  const [hasProceededToPayment, setHasProceededToPayment] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [loginModal, setLoginModal] = React.useState(false)
  const [registeredUser, setRegisteredUser] = React.useState(false)
  const [shippingSameAsBilling, setShippingSameAsBilling] = React.useState(true)
  const [shippingData, setShippingData] = React.useState<undefined | ShippingInfo>(undefined)
  const [billingData, setBillingData] = React.useState<BillingInfo>({
    bFirstName: user?.firstName,
    bLastName: user?.lastName,
    bStreetAdress1: user?.shippingAddress.address1,
    bStreetAdress2: user?.shippingAddress.address2,
    bPostalCode: user?.shippingAddress.zipCode,
    bCity: user?.shippingAddress.city,
    bCountry: user?.shippingAddress.country,
    bCountryArea: undefined,
    bPhone: user?.mobilePhoneNumber,
    bCompany: user?.organization.name,
    bEmail: user?.email,
  })
  const [shippingFieldsDisabled, setShippingFieldsDisabled] = React.useState(false)
  const [showPayment, setShowPayment] = React.useState(false)
  const [checkoutId, setCheckoutId] = React.useState()
  const [transactionId, setTransactionId] = React.useState<string>()
  const [clientSecret, setClientSecret] = React.useState()
  const [missingFields, setMissingFields] = React.useState<Array<string>>([])

  const createCheckoutSession = (e?: any) => {
    e?.preventDefault()

    const lines = rentalOrder.map((product) => {
      return {
        quantity: product.amount,
        variantId: product.product.variants[0].id,
      }
    })

    try {
      createCheckoutMutation({
        variables: {
          channel: SELECTED_CHANNEL_SLUG,
          email: billingData?.bEmail,
          lines: lines,
          bFirstName: billingData?.bFirstName,
          bLastName: billingData?.bLastName,
          bStreetAdress1: billingData?.bStreetAdress1,
          bStreetAdress2: billingData?.bStreetAdress2,
          bPostalCode: billingData?.bPostalCode,
          bCity: billingData?.bCity,
          bCountry: billingData?.bCountry,
          bCountryArea: billingData?.bCountryArea,
          bPhone: billingData?.bPhone,
          bCompany: billingData?.bCompany,
          sFirstName: shippingSameAsBilling ? billingData?.bFirstName : shippingData?.sFirstName,
          sLastName: shippingSameAsBilling ? billingData?.bLastName : shippingData?.sLastName,
          sStreetAdress1: shippingSameAsBilling ? billingData?.bStreetAdress1 : shippingData?.sStreetAdress1,
          sStreetAdress2: shippingSameAsBilling ? billingData?.bStreetAdress2 : shippingData?.sStreetAdress2,
          sCity: shippingSameAsBilling ? billingData?.bCity : shippingData?.sCity,
          sPostalCode: shippingSameAsBilling ? billingData?.bPostalCode : shippingData?.sPostalCode,
          sCountry: shippingSameAsBilling ? billingData?.bCountry : shippingData?.sCountry,
          sCountryArea: billingData?.bCountryArea
            ? shippingSameAsBilling
              ? billingData.bCountryArea
              : shippingData?.sCountryArea
            : undefined,
          sPhone: shippingSameAsBilling ? billingData?.bPhone : shippingData?.sPhone,
        },
      })
        .then((response) => {
          let scopedCheckoutId = response.data.checkoutCreate.checkout.id
          orderUpdateMetadataMutation({
            variables: {
              orderId: scopedCheckoutId,
              input: [
                {
                  key: "checkoutType",
                  value: "rental",
                },
                {
                  key: "selectedRentalDate",
                  value: selectedDate,
                },
              ],
            },
          })
            .catch((error) => {
              console.log("something went wrong", error)
            })
            .then((response) => {
              if (response?.data?.orderUpdateMetadata?.errors?.length > 0) {
                setLoading(false)
                setHasProceededToPayment(false)
                setErrors(response?.data?.orderUpdateMetadata?.errors)
              } else {
                setCheckoutId(scopedCheckoutId)
              }
            })
        })
        .catch((error) => {
          console.log(error)
        })
    } catch (error) {
      console.log(error)
    }
  }

  React.useEffect(() => {
    if (checkoutId) {
      if (IS_USING_STRIPE_PLUGIN_GATEWAY) {
        checkoutPaymentCreate({
          checkoutId: checkoutId,
          amount: data.checkoutCreate.checkout.totalPrice.gross.amount,
        }).then((response) => {
          checkoutCompleteMutation({
            variables: {
              checkoutId: checkoutId,
              paymentData: JSON.stringify({
                payment_method_types: ["card"],
              }),
            },
          }).then((response) => {
            setClientSecret(JSON.parse(response.data.checkoutComplete.confirmationData).client_secret)
            setLoading(false)
          })
        })
      } else {
        stripeTransactionInitialize({
          checkoutId: checkoutId,
          amount: data.checkoutCreate.checkout.totalPrice.gross.amount,
          currency: data.checkoutCreate.checkout.totalPrice.gross.currency.toLowerCase(),
          paymentMethodTypes: ["card"],
        }).then((response) => {
          setTransactionId(response.data?.transactionInitialize?.transaction?.id)
          setClientSecret(response.data?.transactionInitialize?.data?.paymentIntent?.client_secret)
          setLoading(false)
        })
      }
    }
  }, [checkoutId])

  React.useEffect(() => {
    if (data?.checkoutCreate?.errors?.length > 0) {
      setLoading(false)
      setHasProceededToPayment(false)
      setErrors(data?.checkoutCreate?.errors)
    }
  }, [data])

  React.useMemo(() => {
    if (user) {
      const bd = {
        bFirstName: user?.firstName,
        bLastName: user?.lastName,
        bStreetAdress1: user?.shippingAddress.address1,
        bStreetAdress2: user?.shippingAddress.address2,
        bPostalCode: user?.shippingAddress.zipCode,
        bCity: user?.shippingAddress.city,
        bCountry: user?.shippingAddress.country,
        bCountryArea: undefined,
        bPhone: user?.mobilePhoneNumber,
        bCompany: user?.organization.name,
        bEmail: user?.email,
      }
      const sd: ShippingInfo = {
        sFirstName: user?.firstName,
        sLastName: user?.lastName,
        sStreetAdress1: user?.shippingAddress.address1,
        sStreetAdress2: user?.shippingAddress.address2,
        sPostalCode: user?.shippingAddress.zipCode,
        sCity: user?.shippingAddress.city,
        sCountry: user?.shippingAddress.country,
        sCountryArea: undefined,
        sPhone: user?.mobilePhoneNumber,
      }

      setBillingData(bd)
      setShippingData(sd)
    }
  }, [user])

  React.useEffect(() => {
    window.scrollTo(0, 0)
    pushAddToCartEvent(rentalOrder)
  }, [])

  const handleProceedToPayment = () => {
    const requiredFields: (keyof BillingInfo)[] = [
      "bFirstName",
      "bLastName",
      "bStreetAdress1",
      "bPostalCode",
      "bCity",
      "bCountry",
      "bPhone",
      "bEmail",
    ]
    const emptyFields = requiredFields.filter((field) => !billingData[field])

    if (emptyFields.length > 0) {
      setMissingFields(emptyFields)
    } else {
      setHasProceededToPayment(true)
      setLoading(true)
      setMissingFields([])
      createCheckoutSession()
      setShowPayment(true)
    }
  }

  const onClickProceedToPayment = (newShippingData?: ShippingInfo) => {
    setErrors([])
    if (newShippingData) {
      setShippingData(newShippingData)
    }
    setShippingFieldsDisabled(true)

    handleProceedToPayment()
  }

  const onClickRegisterAndContinueShipping = (bd: BillingInfo) => {
    setBillingData(bd)
    setRegisteredUser(true)
  }

  return (
    <div>
      <HorizontalSpacer spacing={3} />
      <Container>
        <Row>
          <Col lg={6}>
            <PaddedCard>
              <SmallButton title="Back to rentals" leftIcon={<ArrowBack />} onClick={onClickBack} />
              {!userLoading && !user && (
                <div>
                  <HorizontalSpacer spacing={1.5} />
                  <H1 textAlign="center">Book Rental</H1>
                  <HorizontalSpacer spacing={1.5} />
                  <H4 textAlign="center">Log in to complete your booking</H4>
                  <HorizontalSpacer spacing={1.5} />
                  <Row justify="center">
                    <PrimaryButton title="Log in" onClick={() => setLoginModal(true)} />
                  </Row>
                  <HorizontalSpacer spacing={1.5} />
                  <H2 textAlign="center">Or</H2>
                </div>
              )}
              <HorizontalSpacer spacing={2} />
              {!user ? (
                <RegisterAccountFormRental onUserRegistered={onClickRegisterAndContinueShipping} />
              ) : (
                <BillingAddressForm formData={billingData} setFormData={setBillingData} missingFields={missingFields} />
              )}
              {registeredUser && (
                <div>
                  {
                    <RentalShippingForm
                      onClickProceedToPayment={onClickProceedToPayment}
                      onPressSameAsBilling={() => setShippingSameAsBilling(!shippingSameAsBilling)}
                      fieldsDisabled={shippingFieldsDisabled}
                      shippingSameAsBilling={shippingSameAsBilling}
                      hasProceededToPayment={hasProceededToPayment}
                    />
                  }
                </div>
              )}
              {user && (
                <div>
                  {
                    <RentalShippingForm
                      onClickProceedToPayment={onClickProceedToPayment}
                      onPressSameAsBilling={() => setShippingSameAsBilling(!shippingSameAsBilling)}
                      shippingSameAsBilling={shippingSameAsBilling}
                      user={user}
                      fieldsDisabled={shippingFieldsDisabled}
                      hasProceededToPayment={hasProceededToPayment}
                    />
                  }
                </div>
              )}
              {errors.length > 0 && (
                <div>
                  {errors.map((error: SaleorError) => (
                    <BodyMedium color={theme.colors.red} key={error.field}>
                      {error.field} {error.message}
                    </BodyMedium>
                  ))}
                </div>
              )}

              {loading && <LoadingSpinner />}

              {showPayment &&
                checkoutId &&
                clientSecret &&
                (IS_USING_STRIPE_PLUGIN_GATEWAY || transactionId) &&
                (IS_USING_STRIPE_PLUGIN_GATEWAY ? (
                  <div>
                    <HorizontalSpacer spacing={2} />
                    <StripePluginWidget
                      cart={rentalOrder}
                      checkoutId={checkoutId}
                      clientSecret={clientSecret}
                      onClearCart={() => null}
                    />
                  </div>
                ) : (
                  <div>
                    <HorizontalSpacer spacing={2} />
                    <StripeAppWidget
                      cart={rentalOrder}
                      checkoutId={checkoutId}
                      transactionId={transactionId!}
                      clientSecret={clientSecret}
                      onClearCart={() => null}
                    />
                  </div>
                ))}
            </PaddedCard>
          </Col>
          <Col lg={6}>
            <PaddedCard>
              <H2>Summary</H2>
              <HorizontalSpacer spacing={1.5} />
              <RentalOrderSummary rentalOrder={rentalOrder} selectedDate={selectedDate} />
            </PaddedCard>
            <HorizontalSpacer spacing={2} />
            <PaddedCard>
              <SupportInfo />
            </PaddedCard>
          </Col>
        </Row>
      </Container>
      <HorizontalSpacer spacing={3} />

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {loginModal && (
          <Modal open={loginModal} onClose={() => setLoginModal(!loginModal)} backdropColor="#D5E2EAe1" closeIcon>
            <LoginModalContainer onCloseModal={() => setLoginModal(false)} />
          </Modal>
        )}
      </AnimatePresence>
    </div>
  )
}

export default BookRentalPage
